import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Code2,
  FileDown,
  FileText,
  FlaskConical,
  GraduationCap,
  Hash,
  History,
  type LucideIcon,
  NotebookPen,
  Pencil,
  Printer,
  Scale,
  Search,
  Share2,
  Tag as TagIcon,
  User,
  Workflow,
  X,
} from 'lucide-react';
import {
  KB_CATEGORIES,
  KB_DOCUMENTS,
  kbDocById,
  kbDocCountByCategory,
  type KbDocument,
} from '../testData/knowledgeBase';
import { Select } from '../components/ui/Select';
import { KbMarkdown, parseMarkdown, type TocEntry } from '../components/knowledgeBase/KbMarkdown';
import { searchKb, type KbSearchHit } from '../components/knowledgeBase/kbSearch';
import { useTestMode } from '../components/testMode/TestModeContext';
import { useI18n } from '../contexts/I18nContext';

// Resolve the icon name stored on each category to a real lucide component.
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  Workflow,
  Code2,
  FileText,
  NotebookPen,
  GraduationCap,
  Scale,
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ro-RO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// --- Left sidebar: category tree -------------------------------------------

function CategoryTree({
  activeDocId,
  onSelect,
}: {
  activeDocId: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useI18n();
  // Track which categories are expanded; default open the one holding the
  // active document so the selection is always visible.
  const activeCategoryId = kbDocById(activeDocId)?.categoryId;
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const c of KB_CATEGORIES) initial[c.id] = true;
    return initial;
  });

  useEffect(() => {
    if (activeCategoryId) setOpen((o) => ({ ...o, [activeCategoryId]: true }));
  }, [activeCategoryId]);

  return (
    <nav className="space-y-1">
      {KB_CATEGORIES.map((cat) => {
        const Icon = CATEGORY_ICONS[cat.icon] ?? FileText;
        const docs = KB_DOCUMENTS.filter((d) => d.categoryId === cat.id);
        const isOpen = open[cat.id];

        return (
          <div key={cat.id}>
            <button
              type="button"
              onClick={() => setOpen((o) => ({ ...o, [cat.id]: !o[cat.id] }))}
              className="group flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm font-semibold text-foreground transition-colors hover:bg-sidebar-accent"
            >
              {isOpen ? (
                <ChevronDown size={15} className="shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight size={15} className="shrink-0 text-muted-foreground" />
              )}
              <Icon size={16} className="shrink-0 text-brand-500" />
              <span className="flex-1 truncate">{cat.label}</span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {cat.externalTo ? '↗' : kbDocCountByCategory(cat.id)}
              </span>
            </button>

            {isOpen && (
              <div className="ml-3 mt-0.5 space-y-0.5 border-l border-border pl-2">
                {cat.externalTo ? (
                  <Link
                    to={cat.externalTo}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground"
                  >
                    <FileText size={14} className="shrink-0 opacity-70" />
                    <span className="truncate">{t('kb.viewAggregated')}</span>
                  </Link>
                ) : (
                  docs.map((doc) => {
                    const active = doc.id === activeDocId;
                    return (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={() => onSelect(doc.id)}
                        className={[
                          'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                          active
                            ? 'bg-brand-50 font-medium text-brand-600'
                            : 'text-muted-foreground hover:bg-sidebar-accent hover:text-foreground',
                        ].join(' ')}
                      >
                        <FileText
                          size={14}
                          className={active ? 'shrink-0 text-brand-500' : 'shrink-0 opacity-70'}
                        />
                        <span className="truncate">{doc.title}</span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

// --- Search overlay --------------------------------------------------------

function highlightMatch(text: string, query: string) {
  const q = query.trim();
  if (!q) return text;
  const terms = q.split(/\s+/).filter((t) => t.length >= 2);
  if (!terms.length) return text;
  const re = new RegExp(
    `(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'gi',
  );
  const parts = text.split(re);
  return parts.map((part, i) =>
    re.test(part) ? (
      <mark key={i} className="rounded bg-amber-400/30 px-0.5 text-foreground">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function SearchResultRow({
  hit,
  query,
  onOpen,
}: {
  hit: KbSearchHit;
  query: string;
  onOpen: (id: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(hit.doc.id)}
      className="flex w-full flex-col gap-1 rounded-lg border border-transparent px-3 py-2.5 text-left transition-colors hover:border-border hover:bg-muted/40"
    >
      <div className="flex items-center gap-2">
        <FileText size={15} className="shrink-0 text-brand-500" />
        <span className="font-medium text-foreground">{highlightMatch(hit.doc.title, query)}</span>
        <span className="ml-auto rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-600">
          {hit.categoryLabel}
        </span>
      </div>
      <p className="pl-7 text-sm text-muted-foreground">{highlightMatch(hit.snippet, query)}</p>
      <div className="flex items-center gap-3 pl-7 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <User size={11} /> {hit.doc.author}
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock size={11} /> {formatDate(hit.doc.updatedAt)}
        </span>
      </div>
    </button>
  );
}

function SearchBar({ onOpenDoc }: { onOpenDoc: (id: string) => void }) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [categoryId, setCategoryId] = useState<string>('');
  const [author, setAuthor] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  const authors = useMemo(
    () => [...new Set(KB_DOCUMENTS.map((d) => d.author))].sort((a, b) => a.localeCompare(b)),
    [],
  );

  const hasFilters = Boolean(categoryId || author);
  const results = useMemo(
    () =>
      query.trim() || hasFilters
        ? searchKb(query, {
            categoryId: categoryId || undefined,
            author: author || undefined,
          })
        : [],
    [query, categoryId, author, hasFilters],
  );

  // Close the dropdown on outside click.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const showDropdown = focused && (query.trim().length > 0 || hasFilters);

  const openDoc = (id: string) => {
    onOpenDoc(id);
    setFocused(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="ui-field-label">{t('common.search')}</label>
          <div className="relative">
            <Search
              size={17}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              placeholder={t('kb.search')}
              className="w-full rounded-lg border border-border bg-background py-2.5 pl-10 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-300"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={t('kb.clearSearch')}
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        {/* Filters — picking a value (re)opens the results dropdown, mirroring
            the old focus behavior of the native selects. */}
        <Select
          label={t('common.category')}
          clearable
          value={categoryId}
          onChange={(v) => {
            setCategoryId(v);
            setFocused(true);
          }}
          placeholder={t('kb.allCategories')}
          options={KB_CATEGORIES.filter((c) => !c.externalTo).map((c) => ({
            value: c.id,
            label: c.label,
          }))}
          className="w-44"
        />
        <Select
          label={t('common.author')}
          clearable
          value={author}
          onChange={(v) => {
            setAuthor(v);
            setFocused(true);
          }}
          placeholder={t('kb.allAuthors')}
          options={authors.map((a) => ({ value: a, label: a }))}
          className="w-44"
        />
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-[28rem] overflow-y-auto rounded-xl border border-border bg-popover p-2 shadow-xl">
          <div className="flex items-center justify-between px-2 py-1 text-xs text-muted-foreground">
            <span>
              {t(results.length === 1 ? 'kb.resultRanked' : 'kb.resultsRanked', {
                count: results.length,
              })}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 font-medium text-brand-600">
              <Search size={11} /> {t('kb.semantic')}
            </span>
          </div>
          {results.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t('kb.noResults')}
            </p>
          ) : (
            <div className="space-y-0.5">
              {results.map((hit) => (
                <SearchResultRow key={hit.doc.id} hit={hit} query={query} onOpen={openDoc} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Document viewer -------------------------------------------------------

function MetaItem({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
      <Icon size={14} className="text-brand-500" />
      {label}
    </span>
  );
}

function TableOfContents({ toc }: { toc: TocEntry[] }) {
  const { t } = useI18n();
  if (toc.length === 0) return null;
  return (
    <div className="rounded-xl border border-border bg-card/50 p-4">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Hash size={13} /> {t('kb.toc')}
      </p>
      <ul className="space-y-1.5 text-sm">
        {toc.map((entry) => (
          <li key={entry.id} className={entry.level === 3 ? 'pl-3' : ''}>
            <a
              href={`#${entry.id}`}
              className="block truncate text-muted-foreground transition-colors hover:text-brand-600"
            >
              {entry.text}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
  primary,
}: {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
        primary
          ? 'bg-brand-600 text-white hover:bg-brand-700'
          : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground',
      ].join(' ')}
    >
      <Icon size={14} /> {label}
    </button>
  );
}

function DocumentViewer({ doc, onOpenDoc }: { doc: KbDocument; onOpenDoc: (id: string) => void }) {
  const { t } = useI18n();
  const { toc } = useMemo(() => parseMarkdown(doc.content), [doc.content]);
  const category = KB_CATEGORIES.find((c) => c.id === doc.categoryId);
  const related = doc.related.map((id) => kbDocById(id)).filter((d): d is KbDocument => Boolean(d));

  return (
    <div className="flex gap-8">
      {/* Article column */}
      <article className="min-w-0 flex-1">
        {/* Breadcrumb */}
        <div className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground">
          <BookOpen size={14} />
          <span>KB</span>
          <ChevronRight size={13} />
          <span>{category?.label}</span>
          <ChevronRight size={13} />
          <span className="font-medium text-foreground">{doc.title}</span>
        </div>

        {/* Action toolbar */}
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <ActionButton icon={Pencil} label={t('kb.action.edit')} primary />
          <ActionButton icon={Share2} label={t('kb.action.share')} />
          <ActionButton
            icon={Printer}
            label={t('kb.action.print')}
            onClick={() => window.print()}
          />
          <ActionButton
            icon={FileDown}
            label={t('kb.action.exportPdf')}
            onClick={() => window.print()}
          />
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">
            <History size={13} /> {t('kb.version', { version: doc.version })}
          </span>
        </div>

        {/* Metadata row */}
        <div className="mb-6 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-border bg-card/40 px-4 py-3">
          <MetaItem icon={User} label={doc.author} />
          <MetaItem icon={Calendar} label={t('kb.created', { date: formatDate(doc.createdAt) })} />
          <MetaItem icon={Clock} label={t('kb.updated', { date: formatDate(doc.updatedAt) })} />
        </div>

        {/* Tags */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <TagIcon size={14} className="text-muted-foreground" />
          {doc.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Rendered markdown */}
        <KbMarkdown content={doc.content} />

        {/* Related documents */}
        {related.length > 0 && (
          <div className="mt-12 border-t border-border pt-6">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t('kb.related')}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {related.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onOpenDoc(r.id)}
                  className="flex items-start gap-3 rounded-lg border border-border bg-card/40 p-3 text-left transition-colors hover:border-brand-300 hover:bg-muted/40"
                >
                  <FileText size={16} className="mt-0.5 shrink-0 text-brand-500" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{r.title}</p>
                    <p className="truncate text-xs text-muted-foreground">{r.author}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Table of contents (sticky on wide screens) */}
      <aside className="hidden w-56 shrink-0 xl:block">
        <div className="sticky top-6">
          <TableOfContents toc={toc} />
        </div>
      </aside>
    </div>
  );
}

// --- Page ------------------------------------------------------------------

export function KnowledgeBasePage() {
  const { t } = useI18n();
  const { enabled: testMode } = useTestMode();
  const [activeDocId, setActiveDocId] = useState<string>(KB_DOCUMENTS[0].id);
  const [mobileTreeOpen, setMobileTreeOpen] = useState(false);
  const activeDoc = kbDocById(activeDocId) ?? KB_DOCUMENTS[0];

  // Scroll the article column back to top whenever the document changes.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [activeDocId]);

  return (
    <div className="flex h-full flex-col">
      {/* Header + search */}
      <div className="border-b border-border px-4 py-4 sm:px-6">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <BookOpen size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">{t('kb.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('kb.subtitle')}</p>
          </div>
          {!testMode && (
            <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-500">
              <FlaskConical size={13} /> {t('kb.demoContent')}
            </span>
          )}
          {testMode && (
            <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-brand-300 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-600">
              <FlaskConical size={13} /> Test Mode
            </span>
          )}
        </div>
        <SearchBar onOpenDoc={setActiveDocId} />
      </div>

      {/* Body: tree + viewer. On phones the fixed 288px sidebar would crush the
          article column, so the tree collapses into a toggleable panel above
          the document (CLE-47 mobile pass). */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className="border-b border-border lg:hidden">
          <button
            type="button"
            onClick={() => setMobileTreeOpen((v) => !v)}
            className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-foreground"
          >
            {t('kb.categories')}
            <ChevronDown
              size={16}
              className={`transition-transform ${mobileTreeOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {mobileTreeOpen && (
            <div className="max-h-72 overflow-y-auto px-3 pb-3">
              <CategoryTree
                activeDocId={activeDocId}
                onSelect={(id) => {
                  setActiveDocId(id);
                  setMobileTreeOpen(false);
                }}
              />
            </div>
          )}
        </div>

        <aside className="hidden w-72 shrink-0 overflow-y-auto border-r border-border px-3 py-4 lg:block">
          <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('kb.categories')}
          </p>
          <CategoryTree activeDocId={activeDocId} onSelect={setActiveDocId} />
        </aside>

        <div ref={scrollRef} className="min-w-0 flex-1 overflow-y-auto px-4 py-5 sm:px-8 sm:py-6">
          <DocumentViewer doc={activeDoc} onOpenDoc={setActiveDocId} />
        </div>
      </div>
    </div>
  );
}
